import { NarrowFulfilmentMethodInputType as FulfillmentType } from '@src/graphql-types'
import { OrderabilityFragment } from '../../../fragments/__generated__/OrderabilityFragment'
import { DateifyOutlet } from '../../../utils/fulfilmentTimes/parsers'

interface Outlet {
  isOpen: boolean
  isOnline: boolean
  allowPreorders: boolean
  specialOfferFlags?: {
    deliveryDeal?: string | null
    collectionDeal?: string | null
    tableDeal?: string | null
  }
  displayName: string
  id: string
}

type SpecialOfferFlagsOutlet = DateifyOutlet<
  Pick<Outlet, 'id' | 'specialOfferFlags'> & {
    orderability: OrderabilityFragment
  }
>

const isFulfilmentMethodAvailable = (
  outlet: SpecialOfferFlagsOutlet,
  method: FulfillmentType
): boolean => {
  const { prioritisedCurrentlyLocationAvailableFulfilmentMethods } =
    outlet.orderability

  switch (method) {
    case FulfillmentType.DELIVERY:
      return (
        prioritisedCurrentlyLocationAvailableFulfilmentMethods.includes(
          FulfillmentType.DELIVERY
        ) && Boolean(outlet.specialOfferFlags?.deliveryDeal)
      )
    case FulfillmentType.COLLECTION:
      return (
        prioritisedCurrentlyLocationAvailableFulfilmentMethods.includes(
          FulfillmentType.COLLECTION
        ) && Boolean(outlet.specialOfferFlags?.collectionDeal)
      )
    case FulfillmentType.TABLE:
      return (
        prioritisedCurrentlyLocationAvailableFulfilmentMethods.includes(
          FulfillmentType.TABLE
        ) && Boolean(outlet.specialOfferFlags?.tableDeal)
      )
    default:
      return false
  }
}

const getFulfilmentDeal = (
  outlet: SpecialOfferFlagsOutlet,
  method: FulfillmentType
): string | null | undefined => {
  switch (method) {
    case FulfillmentType.DELIVERY:
      return outlet.specialOfferFlags?.deliveryDeal
    case FulfillmentType.COLLECTION:
      return outlet.specialOfferFlags?.collectionDeal
    case FulfillmentType.TABLE:
      return outlet.specialOfferFlags?.tableDeal
  }
}

export const findValidSpecialOfferFlag = (
  outlet: SpecialOfferFlagsOutlet,
  nonEmptyFulfilmentMethods: FulfillmentType[]
): string | null | undefined => {
  if (!outlet?.specialOfferFlags) return null
  const hasDeliveryAndCollection =
    nonEmptyFulfilmentMethods.includes(FulfillmentType.DELIVERY) &&
    nonEmptyFulfilmentMethods.includes(FulfillmentType.COLLECTION)

  if (
    // prioritize delivery deal if both available regardless of order nonEmptyFulfilmentMethods
    hasDeliveryAndCollection &&
    isFulfilmentMethodAvailable(outlet, FulfillmentType.DELIVERY) &&
    isFulfilmentMethodAvailable(outlet, FulfillmentType.COLLECTION)
  ) {
    return outlet.specialOfferFlags.deliveryDeal
  }

  for (const method of nonEmptyFulfilmentMethods) {
    if (isFulfilmentMethodAvailable(outlet, method)) {
      return getFulfilmentDeal(outlet, method)
    }
  }

  // handles deals for pre-order available cases
  if (
    nonEmptyFulfilmentMethods.length === 1 &&
    nonEmptyFulfilmentMethods[0] === FulfillmentType.DELIVERY &&
    outlet.orderability.delivery.preorder.isAvailableForSelectedDate
  ) {
    return outlet.specialOfferFlags.deliveryDeal
  }
  if (
    nonEmptyFulfilmentMethods.length === 1 &&
    nonEmptyFulfilmentMethods[0] === FulfillmentType.COLLECTION &&
    outlet.orderability.collection.preorder.isAvailableForSelectedDate
  ) {
    return outlet.specialOfferFlags.collectionDeal
  }
  return null
}
